import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../config/api';
import './ContactFormModal.css';

const REASON_KEYS = ['general', 'feedback', 'suggestions', 'driver', 'partner', 'support', 'other'];

const ContactFormModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentBase64, setAttachmentBase64] = useState(null);
  const [attachmentMime, setAttachmentMime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAttachment(null);
      setAttachmentBase64(null);
      setAttachmentMime('');
      return;
    }
    if (!file.type.startsWith('image/')) return;
    setAttachment(file);
    const reader = new FileReader();
    reader.onload = () => {
      setAttachmentBase64(reader.result);
      setAttachmentMime(file.type);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !reason.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        reason: reason.trim(),
        message: message.trim()
      };
      if (attachmentBase64) {
        payload.attachment = attachmentBase64;
        payload.attachmentMime = attachmentMime;
      }
      await axios.post(`${API_URL}/contact`, payload);
      setSuccess(true);
      setName('');
      setEmail('');
      setPhone('');
      setReason('');
      setMessage('');
      setAttachment(null);
      setAttachmentBase64(null);
      setAttachmentMime('');
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(t('contactForm.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-modal-header">
          <h2>{t('contactForm.title')}</h2>
          <button type="button" className="contact-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {success ? (
          <p className="contact-modal-success">{t('contactForm.success')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="contact-form">
            <label>
              {t('contactForm.name')}
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </label>
            <label>
              {t('contactForm.email')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              {t('contactForm.phone')}
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </label>
            <label>
              {t('contactForm.reason')}
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              >
                <option value="">{t('contactForm.reasonPlaceholder')}</option>
                {REASON_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`contactForm.reasons.${key}`)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('contactForm.message')}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('contactForm.messagePlaceholder')}
                rows={4}
              />
            </label>
            <label>
              {t('contactForm.attachImage')}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="contact-form-file"
              />
              {attachment && (
                <span className="contact-form-file-name">{attachment.name}</span>
              )}
            </label>
            {error && <p className="contact-form-error">{error}</p>}
            <button type="submit" className="contact-form-submit" disabled={submitting}>
              {submitting ? t('contactForm.sending') : t('contactForm.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactFormModal;
